// @givmo/auth — signing in through the person's organization (AS-WEB30): the employer's own identity
// provider, by the backend's SP-initiated SAML ceremony, exchanged at the end for a Firebase custom
// token — a FIFTH verb beside OAuth, email/password, passkey sign-in and re-authentication.
//
//   POST /authn/sso/route {email, code_challenge, redirect_uri} → {sso_available, authorize_url}
//   → the browser at `authorize_url` (the broker, then the employer's IdP)
//   → GET /authn/sso/callback → 303 to `redirect_uri` with `handoff_code` (one use, two minutes)
//   → POST /authn/sso/token {handoff_code, code_verifier} → {custom_token} → signInWithCustomToken
//
// This module is the last step only: the exchange and the sign-in. The door, its window and its
// failure states are the consoles' (`@givmo/passkeys`), and the transport is injected, as for the
// passkey sign-in, so this package stays free of any HTTP coupling.
//
// SIGN-IN ONLY — never a step-up: `signInWithCustomToken` ESTABLISHES a session, so whoever completes
// the ceremony becomes the signed-in user (the passkey sign-in's reasoning, `passkeys.ts`). No uid
// binding is needed here for the same mirror-image reason: establishing identity IS the operation.
// The device binding is PKCE: the verifier never left the page that started the ceremony, so a
// handoff code stolen on its way back buys nothing (`pkce.ts`).
//
// ⚠ THE CUSTOM TOKEN IS A BEARER CREDENTIAL, held only as a local between the exchange and
// `signInWithCustomToken`, never on an object, an error or a log (the passkey sign-in's rule).
//
// NO second-factor branch: GCIP raises none on a custom-token sign-in, and the token's `gvm_amr:
// ["sso"]` records how the person proved who they are. The sign-in lock's blocking function never
// sees this path either (custom-token sign-ins do not fire it); the backend's mint is its gate.

import { signInWithCustomToken, type Auth, type UserCredential } from 'firebase/auth';
import { AuthError } from './errors';

/** The routes the ceremony rides on, injected (see the header). Both live on the signed-out mount. */
export interface OrganizationSsoTransport {
  /**
   * `POST /authn/sso/route` — does this address's organization sign in through its own provider,
   * and if so, where to send the browser. Uniform for accounts: no account is read.
   */
  route(params: {
    email: string;
    codeChallenge: string;
    redirectUri: string;
  }): Promise<{ ssoAvailable: boolean; authorizeUrl: string | null }>;
  /**
   * `POST /authn/sso/token` — the handoff code and the PKCE verifier for a Firebase custom token.
   * Rejects with the transport's own refusal (the backend's machine code and status, never the body
   * of a success, which carries the token).
   */
  exchange(params: { handoffCode: string; codeVerifier: string }): Promise<string>;
}

/**
 * Finish an organization sign-in: exchange the handoff for a custom token and sign in with it.
 *
 * A refusal from the exchange is rethrown exactly as the transport raised it, so the door can say
 * which of its states it was (one uniform `SSO_CEREMONY_FAILED` for every failure to prove the
 * ceremony, 429, the dark 404); a Firebase failure of the sign-in itself becomes an {@link AuthError}.
 */
export async function signInWithOrganizationSso(
  auth: Auth,
  transport: OrganizationSsoTransport,
  params: { handoffCode: string; codeVerifier: string },
): Promise<UserCredential> {
  // ⚠ The custom token exists only between these two statements (the header).
  const customToken = await transport.exchange(params);
  try {
    return await signInWithCustomToken(auth, customToken);
  } catch (error) {
    throw AuthError.from(error);
  }
}
